import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatChip } from "@angular/material/chips";
import { CapabilityRegistry, layerHandoffCapabilities } from "../../../../services/capability.registry";
import { FeatureFlagsService } from "../../../../../../viewer-components/services/internal-services/feature-flags.service";
import { ActionService } from "../../../../services/action.service";
import { DebugModeService } from "../../../../services/debug-mode.service";
import { ToolDataStreamService, type ToolDataStreamResult } from "../../../../services/tool-data-stream.service";
import { CardHeaderComponent } from "../../shared/card-header/card-header.component";
import { TypeChipComponent } from "../../shared/type-chip/type-chip.component";
import type { ChipType } from "../../shared/type-chip/type-chip.component";
import { ContentDataViewerComponent } from "../../shared/content-data-viewer/content-data-viewer.component";
import { ContentActionBarComponent } from "../../shared/content-action-bar/content-action-bar.component";
import { contentFormatToResultType, GEO_CONTENT_KINDS, CONTENT_BLOCK_KINDS, resolveBlockRef } from "../../../../model/block.types";
import { resolveLayerContract, LAYER_TYPE_TO_LABEL } from "../../../../model/evidence-layer.types";
import type { ContentBlockData, ContentBlockKind, ResultType } from "../../../../model/block.types";
import type { DataResult, ActionContext, DataContentType } from "../../../../model/data.types";
import type { ActionId, Capability } from "../../../../model/action.types";
import { FileBlockCardComponent } from "../file-block-card/file-block-card.component";
import { GenericContentCardComponent } from "../generic-content-card/generic-content-card.component";
import { BlockDebugMetaComponent } from "./block-debug-meta/block-debug-meta.component";

/**
 * Generic card for any content block sharing the `ContentBlockData` shape (10 kinds — see
 * CONTENT_BLOCK_KINDS). Routes by data().ref / data().value, converting to DataResult and
 * rendering via shared viewer + action bar, delegating to FileBlockCardComponent (file ref) or
 * GenericContentCardComponent (unrecognized kind / no payload).
 */
@Component({
  selector: "app-content-block-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCard,
    MatCardContent,
    MatChip,
    CardHeaderComponent,
    TypeChipComponent,
    ContentDataViewerComponent,
    ContentActionBarComponent,
    FileBlockCardComponent,
    GenericContentCardComponent,
    BlockDebugMetaComponent,
  ],
  templateUrl: "./content-block-card.component.html",
  styleUrl: "./content-block-card.component.scss",
})
export class ContentBlockCardComponent {
  kind = input.required<ContentBlockKind>();

  data = input.required<ContentBlockData>();

  /** Block id from NormalizedContentBlock — used to look up SSE payloads. */
  blockId = input.required<string>();

  readonly #registry = inject(CapabilityRegistry);

  readonly #featureFlags = inject(FeatureFlagsService);

  readonly #actions = inject(ActionService);

  readonly #streamSvc = inject(ToolDataStreamService);

  protected readonly debugMode = inject(DebugModeService);

  /** Resolved block ref — the backend may deliver blob refs as a plain URL string (normalized). */
  protected readonly ref = computed(() => resolveBlockRef(this.data().ref));

  protected readonly isKnownKind = computed(() => CONTENT_BLOCK_KINDS.has(this.kind()));

  /** True when there is a value payload, either inline or arrived later via SSE (keyed by blockId). */
  protected readonly hasResult = computed(
    () => this.data().value !== null || this.#streamSvc.payloads().has(this.blockId()),
  );

  protected readonly dataResult = computed((): DataResult => {
    const { format, value } = this.data();

    // When the block arrived with value === null (large payload sent via SSE),
    // fall back to the streamed result block stored by ToolDataStreamService (keyed by block_id).
    const ssePayload: ToolDataStreamResult | undefined =
      value === null ? this.#streamSvc.payloads().get(this.blockId()) : undefined;
    const effectiveValue = value ?? ssePayload?.result_data ?? null;
    const effectiveType: ResultType =
      value !== null ? contentFormatToResultType(format) : ssePayload?.result_type ?? contentFormatToResultType(format);

    switch (effectiveType) {
      case "geojson":
        return { type: "geojson", geojson: effectiveValue };
      case "json":
        return { type: "json", json: effectiveValue };
      case "text":
        return { type: "text", text: String(effectiveValue ?? "") };
      case "url":
        return { type: "url", url: String(effectiveValue ?? "") };
      case "image": {
        // SSE path delivers { base64, format }; LangGraph state path delivers a URL string.
        const imgData = effectiveValue as Record<string, unknown> | null;
        const url =
          imgData && typeof imgData === "object" && "base64" in imgData
            ? `data:image/${imgData.format};base64,${imgData.base64}`
            : String(effectiveValue ?? "");
        return { type: "image", url };
      }
      case "geotiff": {
        // SSE path delivers { base64, format }; LangGraph state path delivers a URL string.
        const tiffData = effectiveValue as Record<string, unknown> | null;
        if (tiffData && typeof tiffData === "object" && "base64" in tiffData) {
          return { type: "geotiff", geotiff: String(tiffData.base64) };
        }
        return { type: "geotiff", url: String(effectiveValue ?? "") };
      }
      case "pdf":
        return { type: "pdf", url: String(effectiveValue ?? "") };
      case "binary":
        return { type: "binary", url: String(effectiveValue ?? "") };
      default:
        return { type: "unknown" };
    }
  });

  protected readonly geojsonFeatureCount = computed((): number | null => {
    const dr = this.dataResult();
    if (dr.type !== "geojson" || !dr.geojson) return null;
    const g = dr.geojson as Record<string, unknown>;
    if (g.type === "FeatureCollection" && Array.isArray(g.features)) {
      return (g.features as unknown[]).length;
    }
    return null;
  });

  protected readonly chipType = computed((): ChipType => {
    const t = this.dataResult().type;
    // Geotiff keeps its own raster identity instead of collapsing to binary. The envelope-level
    // kind is the authoritative signal (value.kind === "geotiff"); data.format === "geotiff" and
    // ref.mime_type === "image/tiff" corroborate it per the backend contract (S5 table).
    if (this.kind() === "geotiff" || t === "geotiff") return "raster";
    if (t === "pdf" || t === "unknown") return "binary";
    return t as ChipType;
  });

  /**
   * Evidence-layer chips (evidence-layer-ui A3, US1/US2): when the block carries the
   * `data.layer` annotation, the type chip's label shows the layer type (LAYER_TYPE_TO_LABEL)
   * and the entity renders as a second chip. Blocks without a layer keep today's exact chip:
   * both computeds yield undefined, so the label input stays unset and no second chip renders.
   * The contract goes through the sanctioned accessor — `layer` is never read raw
   * (block.types.ts), which also normalizes unknown types to "default".
   */
  protected readonly layerContract = computed(() => resolveLayerContract(this.data()));

  protected readonly layerTypeLabel = computed((): string | undefined => {
    const layer = this.layerContract();
    return layer ? LAYER_TYPE_TO_LABEL[layer.type] : undefined;
  });

  protected readonly layerEntity = computed((): string | undefined => this.layerContract()?.entity ?? undefined);

  protected readonly capabilities = computed(() => {
    const dr = this.dataResult();
    const all = this.#registry.getAllCapabilities(dr.type as DataContentType);
    const base = dr.type === "geojson" && !this.#actions.hasExtractableGeometry(dr.geojson) ? all.filter((c) => c.action !== "zoom_to") : all;
    // Viewer-handoff (F1): contract blocks with inline value gain the embeddings/elements actions.
    return layerHandoffCapabilities(this.data(), this.#featureFlags, this.#mapCapabilitiesForKind(base));
  });

  protected readonly actionContext = computed((): ActionContext => {
    const dr = this.dataResult();
    // US3 population: the block's data always rides along in the context — ActionService
    // routes contract-bearing blocks through EvidenceLayerService and keeps the legacy
    // add_layer path for blocks without one. Population here is unconditional and cheap.
    const ctx: ActionContext = { dataResult: dr, block: this.data() };
    if (dr.geojson) ctx.geometry = dr.geojson;
    if (dr.url) ctx.url = dr.url;
    if (dr.text) ctx.textContent = dr.text;
    if (dr.filename) ctx.filename = dr.filename;

    const { metadata } = this.data();
    if (GEO_CONTENT_KINDS.has(this.kind()) && metadata) {
      const bbox = metadata["bbox"] as [number, number, number, number] | undefined;
      if (bbox) ctx.bounds = bbox;
      // Canonical shape for metadata.center is not specified by the ARCH doc beyond the field
      // name — assumed [lon, lat] to match the canonical metadata.bbox array convention.
      const center = metadata["center"] as [number, number] | undefined;
      if (center) ctx.point = { lng: center[0], lat: center[1], zoom: metadata["zoom_hint"] as number | undefined };
    }
    return ctx;
  });

  protected onAction(action: ActionId): void {
    this.#actions.execute(action, this.actionContext());
  }

  /** Adds zoom_to/move_to quick actions (backed by metadata.bbox/metadata.center) for geo content kinds. */
  #mapCapabilitiesForKind(base: Capability[]): Capability[] {
    const { metadata } = this.data();
    if (!GEO_CONTENT_KINDS.has(this.kind()) || !metadata) return base;

    const extras: Capability[] = [];
    if (metadata["bbox"] && !base.some((c) => c.action === "zoom_to")) {
      extras.push({ action: "zoom_to", label: "Zoom to", icon: "zoom_out_map", primary: base.length === 0 });
    }
    if (metadata["center"] && !base.some((c) => c.action === "move_to")) {
      extras.push({ action: "move_to", label: "Center", icon: "my_location" });
    }
    return extras.length > 0 ? [...base, ...extras] : base;
  }
}
